import { DefaultError, QueryClient, QueryKey, useQuery, UseQueryOptions, UseQueryResult } from "@tanstack/react-query";
import { corporateCMSApi } from "~/Services";
import { PageHeading } from "../MediaCentre/Media.hook";
interface ContentData {
  title: string;
  description: string;
  image: string;
  vision: {
    title: string;
    description: string;
    image: string;
  };
  brandPromise: {
    title: string;
    description: string;
    image: string;
  };
  nextPage: {
    name: string;
    link: string;
  };
}

interface OurBrand {
  id: number;
  field_content_english: { data: ContentData };
  field_content_malay: { data: ContentData };
  field_content_mandarin: { data: ContentData };
  field_page: string;
  published: boolean;
  createdAt: string;
  updatedAt: string;
}

interface ProfileData {
  items: Array<{
    tab: {
      name: string;
      index: string;
    };
    body: string;
  }>;
  nextPage: {
    name: string;
    link: string;
  };
  timelineFrame: string;
  labelData: object;
}

interface OurProfile {
  id: number;
  field_content_english: { data: ProfileData };
  field_content_malay: { data: ProfileData };
  field_content_mandarin: { data: ProfileData };
  field_page: string;
  published: boolean;
  createdAt: string;
  updatedAt: string;
}

interface FieldContent {
  data: {
    items: Array<{
      tab: {
        name: string;
        index: string;
      };
      tabData?: {
        items?: Array<{
          title: string;
          key: string;
          markup: string;
        }>;
      };
    }>;
    nextPage: {
      name: string;
      link: string;
    };
  };
}

interface OurPolicies {
  id: number;
  field_content_english: FieldContent;
  field_content_malay: FieldContent;
  field_content_mandarin: FieldContent;
  field_page: string;
  published: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface AirportMetric {
  "IATA:SAW, ICAO LTFJ": string;
  "IATA/ICAO code": string;
  Latitude: string;
  Elevation: string;
  Longitude: string;
  Runway: number;
  Capacity: string;
}

export interface AirportDescription {
  title: string;
  key: string;
  image: string;
  metrics: AirportMetric;
  descriptions: string[];
}

interface Tab {
  name: string;
  index: string;
}

export interface TabData {
  items: AirportDescription[];
}

export interface AirportFieldContent {
  data: {
    items: Array<{
      tab: Tab;
      tabData: TabData;
    }>;
    nextPage: {
      name: string;
      link: string;
    };
  };
}

export interface OurAirports {
  id: number;
  field_content_english: AirportFieldContent;
  field_content_malay: AirportFieldContent;
  field_content_mandarin: AirportFieldContent;
  field_page: string;
  published: boolean;
  createdAt: string;
  updatedAt: string;
}

export type Language = "field_content_english" | "field_content_malay" | "field_content_mandarin";

export interface NextPage {
  name: string;
  link: string;
}

export type MetricKey = keyof AirportMetric;
export type NextPageKey = keyof NextPage;

const fetchData = async <T>(url: string): Promise<T> => {
  const response = await corporateCMSApi.get<T>(url);
  return response.data;
};

const createDataHook =
  <T>(key: string, url: string) =>
  (
    options?: UseQueryOptions<T, DefaultError, T, QueryKey>,
    queryClient?: QueryClient
  ): UseQueryResult<T, DefaultError> => {
    return useQuery<T, DefaultError, T, QueryKey>(
      {
        queryKey: [key],
        enabled: false,
        queryFn: () => fetchData<T>(url),
        refetchOnWindowFocus: false,
        ...options,
      },

      queryClient
    );
  };

const useOurBrand = createDataHook<OurBrand[]>(
  "ourBrand",
  `/api/page-content/all-fields?pageName=about-us/corporate-profile/our-brand`
);
const useOurProfile = createDataHook<OurProfile[]>(
  "ourProfile",
  `/api/page-content/all-fields?pageName=about-us/corporate-profile/our-profile`
);
const useOurPolicies = createDataHook<OurPolicies[]>(
  "ourPolicies",
  `/api/page-content/all-fields?pageName=about-us/corporate-profile/our-policies`
);
const useOurAirports = createDataHook<OurAirports[]>(
  "ourAirports",
  `/api/page-content/all-fields?pageName=about-us/corporate-profile/our-airports`
);

const useOurBrandPageHeading = (
  options?: UseQueryOptions<PageHeading[], DefaultError, PageHeading[], QueryKey>,
  queryClient?: QueryClient
): UseQueryResult<PageHeading[], DefaultError> => {
  return useQuery<PageHeading[], DefaultError, PageHeading[], QueryKey>(
    {
      queryKey: ["ourBrandPageHeading"],
      enabled: false,
      queryFn: async (): Promise<PageHeading[]> => {
        const response = await corporateCMSApi.get<PageHeading[]>(
          `/api/page-heading/all-fields?pageName=about-us/corporate-profile/our-brand`
        );
        return response.data;
      },
      refetchOnWindowFocus: false,
      ...options,
    },
    queryClient
  );
};

const useOurProfilePageHeading = (
  options?: UseQueryOptions<PageHeading[], DefaultError, PageHeading[], QueryKey>,
  queryClient?: QueryClient
): UseQueryResult<PageHeading[], DefaultError> => {
  return useQuery<PageHeading[], DefaultError, PageHeading[], QueryKey>(
    {
      queryKey: ["ourProfilePageHeading"],
      enabled: false,
      queryFn: async (): Promise<PageHeading[]> => {
        const response = await corporateCMSApi.get<PageHeading[]>(
          `/api/page-heading/all-fields?pageName=about-us/corporate-profile/our-profile`
        );
        return response.data;
      },
      refetchOnWindowFocus: false,
      ...options,
    },
    queryClient
  );
};

const useOurAirportsPageHeading = (
  options?: UseQueryOptions<PageHeading[], DefaultError, PageHeading[], QueryKey>,
  queryClient?: QueryClient
): UseQueryResult<PageHeading[], DefaultError> => {
  return useQuery<PageHeading[], DefaultError, PageHeading[], QueryKey>(
    {
      queryKey: ["ourAirportsPageHeading"],
      enabled: false,
      queryFn: async (): Promise<PageHeading[]> => {
        const response = await corporateCMSApi.get<PageHeading[]>(
          `/api/page-heading/all-fields?pageName=about-us/corporate-profile/our-airports`
        );
        return response.data;
      },
      refetchOnWindowFocus: false,
      ...options,
    },
    queryClient
  );
};

const useOurPoliciesPageHeading = (
  options?: UseQueryOptions<PageHeading[], DefaultError, PageHeading[], QueryKey>,
  queryClient?: QueryClient
): UseQueryResult<PageHeading[], DefaultError> => {
  return useQuery<PageHeading[], DefaultError, PageHeading[], QueryKey>(
    {
      queryKey: ["ourPoliciesPageHeading"],
      enabled: false,
      queryFn: async (): Promise<PageHeading[]> => {
        const response = await corporateCMSApi.get<PageHeading[]>(
          `/api/page-heading/all-fields?pageName=about-us/corporate-profile/our-policies`
        );
        return response.data;
      },
      refetchOnWindowFocus: false,
      ...options,
    },
    queryClient
  );
};

export {
  useOurAirports,
  useOurAirportsPageHeading,
  useOurBrand,
  useOurBrandPageHeading,
  useOurPolicies,
  useOurPoliciesPageHeading,
  useOurProfile,
  useOurProfilePageHeading,
};
